import { useState, useEffect } from 'react';
import { useUserData } from '../hooks/useUserData';
import Logo from '../components/Logo';
import Navigation from '../components/Navigation';
import { BanknotesIcon, CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { 
  Bars3Icon, 
  XMarkIcon,
  HomeIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  CreditCardIcon,
  UserCircleIcon,
  CogIcon,
  CurrencyEuroIcon,
  ChartPieIcon,
  BuildingOfficeIcon,
  CircleStackIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
  ArrowUpCircleIcon,
  ArrowDownCircleIcon,
  QrCodeIcon,
  WalletIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ClockIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { usePaymentSettings } from '../firebase/paymentSettings';
import { QRCodeSVG } from 'qrcode.react';
import CryptoMarketData from '../components/CryptoMarketData';
import PortfolioPerformance from '../components/PortfolioPerformance';
import CryptoWidgets from '../components/CryptoWidgets';
import CryptoPriceTicker from '../components/CryptoPriceTicker';
import TradingViewWidget from '../components/TradingViewWidget';
import CryptoPriceWidget from '../components/CryptoPriceWidget';
import { useAuth } from '../contexts/AuthContext';
import OTPVerification from '../components/OTPVerification';
import { addDoc, collection, doc, updateDoc, increment, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

// Add this helper function at the top of the file, outside the component
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInYears > 0) {
    return `${diffInYears} year${diffInYears > 1 ? 's' : ''}`
  } else if (diffInMonths > 0) {
    return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''}`
  } else {
    return `${diffInDays} day${diffInDays !== 1 ? 's' : ''}`
  }
};

function UserDashboard() {
  const { userData, loading, error } = useUserData();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [notifications, setNotifications] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('crypto');
  const [withdrawMethod, setWithdrawMethod] = useState('crypto');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const { currentUser, isAdmin } = useAuth();
  const [depositMethods, setDepositMethods] = useState({
    crypto: {
      title: "Cryptocurrency",
      description: "Fast and secure crypto deposits",
      methods: []
    },
    cashapp: {
      title: "CashApp",
      description: "Quick and easy mobile payments",
      cashtag: "",
      instructions: []
    },
    paypal: {
      title: "PayPal",
      description: "Safe and instant transfers",
      email: "",
      instructions: []
    },
    bank: {
      title: "Bank Transfer",
      description: "Traditional bank wire transfer",
      details: {}
    }
  });
  const [showInvestmentsModal, setShowInvestmentsModal] = useState(false);
  const [showStatCards, setShowStatCards] = useState(false); // Collapsible state for original stat cards - collapsed by default

  const cryptoData = [
    { name: 'BTC', value: 45000, change: '+5.2%' },
    { name: 'ETH', value: 3200, change: '+3.8%' },
    { name: 'SOL', value: 120, change: '-2.1%' },
  ];

  const portfolioData = [
    { name: 'Stocks', value: 40 },
    { name: 'Crypto', value: 30 },
    { name: 'Real Estate', value: 20 },
    { name: 'Cash', value: 10 },
  ];

  const COLORS = ['#4F46E5', '#06B6D4', '#10B981', '#F59E0B'];

  const performanceData = [
    { month: '2024-01-01', value: 2000, profit: 1200 },
    { month: '2024-02-01', value: 3500, profit: 2100 },
    { month: '2024-03-01', value: 5000, profit: 3000 },
    { month: '2024-04-01', value: 7000, profit: 4200 },
    { month: '2024-05-01', value: 9500, profit: 5700 },
    { month: '2024-06-01', value: 12000, profit: 7200 }
  ];

  const PORTFOLIO_ITEMS = [
    { 
      name: 'Stocks', 
      key: 'stocks',
      icon: ArrowTrendingUpIcon, 
      color: '#10B981' // Green
    },
    { 
      name: 'Crypto', 
      key: 'crypto',
      icon: CircleStackIcon, 
      color: '#F59E0B' // Amber
    },
    { 
      name: 'Real Estate', 
      key: 'realEstate',
      icon: BuildingOfficeIcon, 
      color: '#3B82F6' // Blue
    },
    { 
      name: 'Cash', 
      key: 'cash',
      icon: CurrencyDollarIcon, 
      color: '#EC4899' // Pink
    }
  ];

  const withdrawMethods = {
    crypto: {
      title: "Cryptocurrency",
      description: "Fast crypto withdrawals",
      options: [
        { name: "Bitcoin (BTC)", fee: "0.0005 BTC" },
        { name: "Ethereum (ETH)", fee: "0.01 ETH" },
        { name: "USDT (TRC20)", fee: "1 USDT" }
      ]
    },
    bank: {
      title: "Bank Transfer",
      description: "2-5 business days",
      fee: "$25 wire fee",
      requirements: [
        "Bank Name",
        "Account Number",
        "Routing Number",
        "Account Holder Name",
        "Bank Address"
      ]
    },
    cashapp: {
      title: "CashApp",
      description: "Instant withdrawals",
      fee: "1.5%",
      limit: "$7,500 per week"
    },
    paypal: {
      title: "PayPal",
      description: "Instant withdrawals",
      fee: "2.9% + $0.30",
      limit: "$10,000 per transaction"
    }
  };

  const paymentSettings = usePaymentSettings();

  useEffect(() => {
    if (paymentSettings) {
      setDepositMethods({
        crypto: {
          title: "Cryptocurrency",
          description: "Fast and secure crypto deposits",
          methods: [
            {
              name: paymentSettings.crypto.bitcoin.name,
              address: paymentSettings.crypto.bitcoin.address
            },
            {
              name: paymentSettings.crypto.ethereum.name,
              address: paymentSettings.crypto.ethereum.address
            },
            {
              name: paymentSettings.crypto.usdt.name,
              address: paymentSettings.crypto.usdt.address
            }
          ]
        },
        cashapp: {
          title: "CashApp",
          description: "Quick and easy mobile payments",
          cashtag: paymentSettings.cashapp.cashtag,
          instructions: paymentSettings.cashapp.instructions
        },
        paypal: {
          title: "PayPal",
          description: "Safe and instant transfers",
          email: paymentSettings.paypal.email,
          instructions: paymentSettings.paypal.instructions
        },
        bank: {
          title: "Bank Transfer",
          description: "Traditional bank wire transfer",
          details: paymentSettings.bank.details
        }
      });
    }
  }, [paymentSettings]);

  // Add access control effect
  useEffect(() => {
    if (isAdmin) {
      navigate('/admin', { replace: true });
    }
  }, [isAdmin, navigate]);

  // Add effect to handle URL query parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab && ['dashboard', 'deposit', 'withdraw', 'notifications'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [location.search]);

  // Add this effect after userData is available and currentUser exists
  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'withdrawalRequests'),
      where('userId', '==', currentUser.uid)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach(change => {
        const data = change.doc.data();
        if (change.type === 'modified') {
          if (data.status === 'approved') {
            setNotifications(prev => [
              {
                id: `withdrawal-approved-${change.doc.id}`,
                type: 'withdrawal-approved',
                title: 'Withdrawal Approved',
                message: `Your withdrawal request for $${data.amount?.toLocaleString?.() || data.amount} has been approved.`,
                timestamp: new Date(),
                read: false
              },
              ...prev
            ]);
          } else if (data.status === 'rejected') {
            setNotifications(prev => [
              {
                id: `withdrawal-rejected-${change.doc.id}`,
                type: 'withdrawal-rejected',
                title: 'Withdrawal Rejected',
                message: `Your withdrawal request for $${data.amount?.toLocaleString?.() || data.amount} was rejected. Please contact support for details.`,
                timestamp: new Date(),
                read: false
              },
              ...prev
            ]);
          }
        }
      });
    });
    return () => unsubscribe();
  }, [currentUser]);

  const TopNav = () => (
    <nav className="lg:hidden bg-white shadow-lg fixed w-full top-0 z-50">
      <div className="px-4">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Logo darkMode={false} />
          </div>
          
          {/* Mobile menu button */}
          <div className="flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none"
            >
              {mobileMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" />
              ) : (
                <Bars3Icon className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu - Positioned below the header */}
      <div className={`${mobileMenuOpen ? 'block' : 'hidden'} absolute w-full bg-white shadow-lg`}>
        <div className="pt-2 pb-3 space-y-1">
          <MobileNavLink 
            icon={<UserCircleIcon className="w-5 h-5" />} 
            text="Dashboard" 
            active={activeTab === 'dashboard'}
            onClick={() => {
              setActiveTab('dashboard');
              setMobileMenuOpen(false);
            }}
          />
          <MobileNavLink 
            icon={<ArrowUpCircleIcon className="w-5 h-5" />} 
            text="Deposit" 
            active={activeTab === 'deposit'}
            onClick={() => {
              setActiveTab('deposit');
              setMobileMenuOpen(false);
            }}
          />
          <MobileNavLink 
            icon={<ArrowDownCircleIcon className="w-5 h-5" />} 
            text="Withdraw" 
            active={activeTab === 'withdraw'}
            onClick={() => {
              setActiveTab('withdraw');
              setMobileMenuOpen(false);
            }}
          />
          <MobileNavLink 
            icon={<BellIcon className="w-5 h-5" />} 
            text="Notifications" 
            active={activeTab === 'notifications'}
            badge={notifications.filter(n => !n.read).length}
            onClick={() => {
              setActiveTab('notifications');
              setMobileMenuOpen(false);
            }}
          />
          <MobileNavLink 
            icon={<ArrowRightOnRectangleIcon className="w-5 h-5" />} 
            text="Sign Out" 
            onClick={handleSignOut}
          />
        </div>
      </div>
    </nav>
  );

  const NotificationsPanel = () => (
    <div className="p-6">
      <BackButton />
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="border-b border-gray-200">
          <div className="p-4 bg-primary-50">
            <h2 className="text-lg font-semibold text-primary-900">Notifications</h2>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {notifications.map((notification) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 ${notification.read ? 'bg-white' : 'bg-blue-50'}`}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0 pt-0.5">
                  <BellIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {notification.title}
                  </p>
                  <div className="mt-1 text-sm text-gray-500 whitespace-pre-line">
                    {notification.message}
                  </div>
                  <div className="mt-2 text-xs text-gray-400">
                    {new Date(notification.timestamp).toLocaleDateString()} at{' '}
                    {new Date(notification.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
          {notifications.length === 0 && (
            <div className="p-4 text-center text-gray-500">
              No notifications yet
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/login'); // Change this to '/login' for login page, or '/' for homepage
    } catch (error) {
      console.error('Error signing out:', error);
      // Optionally add error notification here
    }
  };

  const Sidebar = () => (
    <div className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-gradient-to-b from-primary-600 to-primary-800 text-white">
      <div className="flex flex-col w-full">
        <div className="p-4 border-b border-primary-500/30">
          <Logo darkMode={true} />
        </div>
        <nav className="flex-1 p-4 space-y-2">
          <SidebarLink 
            icon={<UserCircleIcon className="w-5 h-5" />} 
            text="Dashboard" 
            active={activeTab === 'dashboard'}
            onClick={() => setActiveTab('dashboard')}
          />
          <SidebarLink 
            icon={<ArrowUpCircleIcon className="w-5 h-5" />} 
            text="Deposit" 
            active={activeTab === 'deposit'}
            onClick={() => setActiveTab('deposit')}
          />
          <SidebarLink 
            icon={<ArrowDownCircleIcon className="w-5 h-5" />} 
            text="Withdraw" 
            active={activeTab === 'withdraw'}
            onClick={() => setActiveTab('withdraw')}
          />
          <SidebarLink 
            icon={<BellIcon className="w-5 h-5" />} 
            text="Notifications" 
            active={activeTab === 'notifications'}
            onClick={() => setActiveTab('notifications')}
            badge={notifications.filter(n => !n.read).length.toString()}
          />
        </nav>
        <div className="p-4 border-t border-primary-500/30">
          <motion.button
            onClick={handleSignOut}
            className="flex items-center space-x-3 p-3 w-full rounded-lg text-white/80 hover:bg-white/10 hover:text-white transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            <span className="font-medium">Sign Out</span>
          </motion.button>
        </div>
      </div>
    </div>
  );

  // Back Button Component
  const BackButton = () => (
    <motion.button
      onClick={() => setActiveTab('dashboard')}
      className="mb-6 inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
      whileHover={{ x: -4 }}
    >
      <ArrowLeftIcon className="w-5 h-5 mr-2" />
      Back to Dashboard
    </motion.button>
  );

  const SidebarLink = ({ icon, text, active, badge, onClick }) => (
    <button
      onClick={onClick}
      className={`flex items-center justify-between p-3 rounded-lg transition-colors w-full ${
        active ? 'bg-white/10 text-white' : 'text-white/80 hover:bg-white/5 hover:text-white'
      }`}
    >
      <div className="flex items-center space-x-3">
        {icon}
        <span className="font-medium">{text}</span>
      </div>
      {badge && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
          {badge}
        </span>
      )}
    </button>
  );

  const MobileNavLink = ({ icon, text, active, badge, onClick }) => (
    <button
      onClick={onClick}
      className={`flex items-center justify-between w-full px-4 py-3 text-base font-medium border-l-4 ${
        active
          ? 'border-primary-600 text-primary-600 bg-primary-50'
          : 'border-transparent text-gray-700 hover:text-primary-600 hover:bg-primary-50 hover:border-primary-600'
      }`}
    >
      <div className="flex items-center space-x-3">
        {icon}
        <span>{text}</span>
      </div>
      {badge && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
          {badge}
        </span>
      )}
    </button>
  );

  const getPortfolioData = () => {
    if (!userData?.portfolio) return [];
    
    return PORTFOLIO_ITEMS.map(item => ({
      name: item.name,
      value: userData.portfolio[item.key] || 0,
      color: item.color,
      icon: item.icon
    }));
  };

  useEffect(() => {
    if (userData?.name) {
      setNotifications([
        {
          id: 'welcome',
          type: 'welcome',
          title: `Welcome ${userData.name}!`,
          message: `Welcome to WealthQuota! Here's how to get started:\n\n1. Make your first deposit by clicking the 'Deposit' tab\n2. Choose from our investment plans to start growing your portfolio\n3. Track your investments in real-time from your dashboard\n4. Withdraw your profits easily when ready\n\nFor any questions or support, please email support@wealthquota.com`,
          timestamp: userData.createdAt || new Date(),
          read: false
        }
      ]);
    }
  }, [userData?.name]);

  // Function to add withdrawal notification
  const addWithdrawalNotification = (withdrawalDetails) => {
    setNotifications(prev => [
      {
        id: `withdrawal-${Date.now()}`,
        type: 'withdrawal',
        title: 'Withdrawal Request Submitted',
        message: `Your withdrawal request for $${withdrawalDetails.amount.toLocaleString()} via ${withdrawalDetails.method} has been submitted and is pending approval. You will be notified once it's processed.`,
        timestamp: new Date(),
        read: false
      },
      ...prev
    ]);
  };

  const DepositPanel = () => {
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [selectedCrypto, setSelectedCrypto] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(() => {
      const savedPlan = localStorage.getItem('selectedPlan');
      return savedPlan ? JSON.parse(savedPlan) : null;
    });

    const plans = [
      {
        name: "Standard Plan",
        price: "$1,000",
        features: [
          "10x Profit in 21 days",
          "2% Welcome bonus",
          "$500 earn $5,000",
          "$2,000 earn $20,000",
          "$3,000 earn $30,000",
          "$4,000 earn $40,000",
          "$5,000 earn $50,000"
        ],
        duration: "21 days",
        type: "Basic portfolio",
        support: "24/7 Email Support"
      },
      {
        name: "Premium Plan",
        price: "$6,000",
        features: [
          "15x Profit in 21 days",
          "3% Welcome bonus",
          "$6,000 earn $90,000",
          "$7,000 earn $105,000",
          "$8,000 earn $120,000",
          "$9,000 earn $135,000",
          "$10,000 earn $150,000"
        ],
        duration: "21 days",
        type: "Enhanced portfolio",
        support: "24/7 Priority Chat Support",
        popular: true
      },
      {
        name: "Advanced Plan",
        price: "$10,000",
        features: [
          "20x Profit in 21 days",
          "5% Welcome bonus",
          "$10,000 earn $200,000",
          "$11,000 earn $220,000",
          "$12,000 earn $240,000",
          "$13,000 earn $260,000",
          "$14,000 earn $280,000",
          "VIP Account Manager",
          "Priority Withdrawals",
          "Exclusive Market Analysis"
        ],
        duration: "21 days",
        type: "VIP Wealth Management",
        support: "Dedicated Account Manager"
      },
      {
        name: "Platinum Plan",
        price: "$25,000",
        features: [
          "25x Profit in 21 days",
          "7% Welcome bonus",
          "$25,000 earn $625,000",
          "$30,000 earn $750,000",
          "$35,000 earn $875,000",
          "$40,000 earn $1,000,000",
          "$45,000 earn $1,125,000",
          "Elite Account Manager",
          "Instant Withdrawals",
          "Advanced Market Analysis",
          "Private Investment Events",
          "Personalized Portfolio Strategy"
        ],
        duration: "21 days",
        type: "Elite Wealth Management",
        support: "24/7 Personal Concierge"
      },
      {
        name: "Diamond Plan",
        price: "$50,000",
        features: [
          "30x Profit in 21 days",
          "10% Welcome bonus",
          "$50,000 earn $1,500,000",
          "$60,000 earn $1,800,000",
          "$70,000 earn $2,100,000",
          "$80,000 earn $2,400,000",
          "$90,000 earn $2,700,000",
          "Executive Account Manager",
          "Instant Priority Withdrawals",
          "Expert Market Analysis",
          "Exclusive Investment Events",
          "Custom Portfolio Strategy",
          "Global Investment Opportunities",
          "Wealth Preservation Consulting"
        ],
        duration: "21 days",
        type: "Executive Wealth Management",
        support: "Dedicated Executive Team"
      }
    ];

    const handlePlanChange = (plan) => {
      setSelectedPlan(plan);
      localStorage.setItem('selectedPlan', JSON.stringify(plan));
    };

    const CryptoView = ({ crypto, onBack }) => (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={onBack}
            className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Payment Methods
          </button>
          <span className="text-sm font-medium text-gray-600">{crypto.name}</span>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm space-y-6">
          {/* QR Code Section */}
          <div className="flex flex-col items-center space-y-4">
            <div className="w-48 h-48 bg-white rounded-lg p-3 border">
              <QRCodeSVG
                value={crypto.address}
                size={176}
                level="H"
                includeMargin={true}
                className="w-full h-full"
              />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{crypto.name} Deposit</h3>
          </div>

          {/* Address Section */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Deposit Address</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={crypto.address}
                readOnly
                className="flex-1 p-3 text-sm bg-gray-50 rounded-lg border font-mono break-all"
              />
              <button
                onClick={() => navigator.clipboard.writeText(crypto.address)}
                className="w-full sm:w-auto px-4 py-2 text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg font-medium transition-colors inline-flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy Address
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Instructions</h4>
            <div className="bg-blue-50 rounded-lg p-4">
              <ul className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                <li>Send only {crypto.name} to this deposit address</li>
                <li>Ensure the network is correct to avoid loss of funds</li>
                
                <li>Your deposit will be credited after network confirmation</li>
                <li>Send proof of payment to support@wealthquota.com</li>
              </ul>
            </div>
          </div>

          {/* Network Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-gray-900">Network Information</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Network: {crypto.name === 'Bitcoin (BTC)' ? 'Bitcoin Network' : 
                           crypto.name === 'Ethereum (ETH)' ? 'ERC20' : 
                           'TRC20'}
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  Processing Time: {crypto.name === 'Bitcoin (BTC)' ? '1 hour' : '15 minutes'} after confirmation
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <div className="p-6">
        <BackButton />
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="p-4 bg-primary-50">
              <h2 className="text-lg font-semibold text-primary-900">Deposit Funds</h2>
            </div>
          </div>
          
          <div className="p-6">
            {/* Selected Plan Section */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selected Investment Plan
              </label>
              <select
                value={selectedPlan?.name || ''}
                onChange={(e) => {
                  const plan = plans.find(p => p.name === e.target.value);
                  handlePlanChange(plan);
                }}
                className="w-full p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Select a plan</option>
                {plans.map((plan, index) => (
                  <option key={index} value={plan.name}>
                    {plan.name} - {plan.price}
                  </option>
                ))}
              </select>
              {selectedPlan && (
                <div className="mt-2 p-3 bg-primary-50 rounded-lg">
                  <p className="text-sm text-primary-800">
                    <span className="font-semibold">Selected Plan:</span> {selectedPlan.name}
                  </p>
                  <p className="text-sm text-primary-800">
                    <span className="font-semibold">Minimum Amount:</span> {selectedPlan.price}
                  </p>
                </div>
              )}
            </div>

            {/* Important Notice */}
            <div className="bg-yellow-100 border-l-4 border-yellow-500 rounded-lg p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationCircleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-yellow-800">Important Notice:</h3>
                  <ul className="mt-2 list-disc list-inside space-y-1 text-yellow-800">
                    <li className="font-medium">Deposits must be completed within 24 hours of initiation</li>
                    <li className="font-medium">Send proof of payment to your consultant or email to support@wealthquota.com</li>
                    <li className="font-medium">Include your account ID in the payment reference/notes for faster processing</li>
                  </ul>
                </div>
              </div>
            </div>

            {selectedCrypto ? (
              <CryptoView 
                crypto={selectedCrypto} 
                onBack={() => setSelectedCrypto(null)} 
              />
            ) : (
              <>
                {/* Payment Method Selection */}
                {!selectedPaymentMethod ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {Object.keys(depositMethods).map((method) => (
                      <motion.button
                        key={method}
                        onClick={() => setSelectedPaymentMethod(method)}
                        className="p-4 rounded-lg border-2 border-gray-200 hover:border-primary-200 transition-all"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <h3 className="text-base sm:text-lg font-semibold mb-1">{depositMethods[method].title}</h3>
                        <p className="text-xs sm:text-sm text-gray-600">{depositMethods[method].description}</p>
                      </motion.button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <button
                      onClick={() => setSelectedPaymentMethod(null)}
                      className="mb-4 inline-flex items-center text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
                    >
                      <ArrowLeftIcon className="w-5 h-5 mr-2" />
                      Back to Payment Methods
                    </button>

                    {selectedPaymentMethod === 'crypto' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {depositMethods.crypto.methods.map((crypto) => (
                          <motion.div
                            key={crypto.name}
                            className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setSelectedCrypto(crypto)}
                          >
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-lg font-semibold">{crypto.name}</h4>
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                            <p className="text-sm text-gray-600">Click to view deposit details</p>
                          </motion.div>
                        ))}
                      </div>
                    )}

                    {selectedPaymentMethod === 'cashapp' && (
                      <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                          <div className="w-full sm:w-32 h-32 bg-primary-100 rounded-lg flex items-center justify-center">
                            <span className="text-2xl sm:text-4xl font-bold text-primary-600">
                              {depositMethods.cashapp.cashtag}
                            </span>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-base sm:text-lg font-semibold mb-3">CashApp Instructions</h4>
                            <ol className="list-decimal list-inside space-y-2 text-sm sm:text-base">
                              {depositMethods.cashapp.instructions.map((instruction, index) => (
                                <li key={index} className="text-gray-600">{instruction}</li>
                              ))}
                            </ol>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedPaymentMethod === 'paypal' && (
                      <div className="space-y-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                          <div className="w-full sm:w-32 h-32 bg-primary-100 rounded-lg flex items-center justify-center">
                            <span className="text-xl sm:text-2xl font-bold text-primary-600 break-all px-2">
                              {depositMethods.paypal.email}
                            </span>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-base sm:text-lg font-semibold mb-3">PayPal Instructions</h4>
                            <ol className="list-decimal list-inside space-y-2 text-sm sm:text-base">
                              {depositMethods.paypal.instructions.map((instruction, index) => (
                                <li key={index} className="text-gray-600">{instruction}</li>
                              ))}
                            </ol>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedPaymentMethod === 'bank' && (
                      <div className="space-y-3">
                        <h4 className="text-base sm:text-lg font-semibold">Bank Transfer Details</h4>
                        {Object.entries(depositMethods.bank.details).map(([key, value]) => (
                          <div key={key} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-white rounded-lg">
                            <span className="text-sm sm:text-base text-gray-600 capitalize mb-1 sm:mb-0">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                            <span className="text-sm sm:text-base font-medium">{value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Processing Time Notice */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Processing Time</h4>
              <p className="text-sm text-gray-600">
                Deposits are typically processed within 1-2 business days.
                Contact support if you need assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const WithdrawPanel = () => {
    const [withdrawMethod, setWithdrawMethod] = useState('');
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [withdrawAddress, setWithdrawAddress] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [notifyByEmail, setNotifyByEmail] = useState(true);
    const [userEmail, setUserEmail] = useState(userData?.email || '');
    const [withdrawStatus, setWithdrawStatus] = useState(null);
    const [showOTP, setShowOTP] = useState(false);
    const [pendingWithdrawal, setPendingWithdrawal] = useState(null);
    const { verifyWithdrawalOTP, currentUser } = useAuth();
    // Add state for method-specific fields
    const [bankDetails, setBankDetails] = useState({
      bankName: '',
      accountNumber: '',
      routingNumber: '',
      accountHolder: '',
      bankAddress: ''
    });
    const [cashappTag, setCashappTag] = useState('');
  
    const handleWithdrawalSubmit = async (e) => {
      e.preventDefault();
      
      // Validation
      if (!withdrawMethod) {
        alert("Please select a withdrawal method");
        return;
      }
      
      if (!withdrawAmount || parseFloat(withdrawAmount) < 100) {
        alert("Please enter a valid amount (minimum $100)");
        return;
      }
  
      // Store withdrawal details and show OTP verification
      let pending = {
        amount: parseFloat(withdrawAmount),
        method: withdrawMethod,
        address: withdrawAddress,
        email: notifyByEmail ? userEmail : null,
        timestamp: new Date().toISOString(),
      };
      if (withdrawMethod === 'bank') {
        pending.bankDetails = { ...bankDetails };
      } else if (withdrawMethod === 'cashapp') {
        pending.cashappTag = cashappTag;
      } else if (withdrawMethod === 'paypal') {
        pending.paypalEmail = withdrawAddress;
      }
      setPendingWithdrawal(pending);
      setShowOTP(true);
    };

    const handleOTPVerify = async (otp) => {
      try {
        setIsProcessing(true);
        await verifyWithdrawalOTP(currentUser.uid, otp);
        
        // Create withdrawal request in Firestore
        const withdrawalRequest = {
          ...pendingWithdrawal,
          userId: currentUser.uid,
          status: 'pending',
          userEmail: currentUser.email,
          timestamp: new Date().toISOString(),
          userDetails: {
            fullName: userData?.fullName || userData?.name || 'Unknown',
            accountLevel: userData?.accountLevel || 'standard',
            country: userData?.country || 'Unknown'
          },
          withdrawalDetails: {
            amount: parseFloat(withdrawAmount),
            method: withdrawMethod,
            address: withdrawAddress || null,
            currency: 'USD'
          }
        };

        // Add withdrawal request to Firestore
        const withdrawalRef = await addDoc(collection(db, 'withdrawalRequests'), withdrawalRequest);
        
        // Update user's pending withdrawals in a separate transaction
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
          'accountDetails.pendingWithdrawals': increment(parseFloat(withdrawAmount))
        });

        // Add notification
        addWithdrawalNotification({
          amount: parseFloat(withdrawAmount),
          method: withdrawMethod
        });

        setWithdrawStatus({ 
          type: 'success', 
          message: 'Withdrawal request submitted successfully!'
        });
        
        // Reset form
        setWithdrawAmount('');
        setWithdrawMethod('');
        setWithdrawAddress('');
        setShowOTP(false);
        setPendingWithdrawal(null);
      } catch (err) {
        console.error('Withdrawal error:', err);
        setWithdrawStatus({ 
          type: 'error', 
          message: err.message || 'Failed to process withdrawal request'
        });
      } finally {
        setIsProcessing(false);
      }
    };

    const handleOTPCancel = () => {
      setShowOTP(false);
      setPendingWithdrawal(null);
      setWithdrawStatus(null);
    };
  
    const resetForm = () => {
      setWithdrawAmount('');
      setWithdrawAddress('');
      setWithdrawStatus(null);
      setShowOTP(false);
      setPendingWithdrawal(null);
      setBankDetails({
        bankName: '',
        accountNumber: '',
        routingNumber: '',
        accountHolder: '',
        bankAddress: ''
      });
      setCashappTag('');
    };
  
    return (
      <div className="p-6">
        <BackButton />
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="p-4 bg-primary-50">
              <h2 className="text-lg font-semibold text-primary-900">Withdraw Funds</h2>
            </div>
          </div>
          
          <div className="p-6">
            {/* Available Balance */}
            <div className="mb-8 bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Available Balance</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${userData?.balance?.toLocaleString() || '0'}
                  </p>
                </div>
                <BanknotesIcon className="w-8 h-8 text-primary-600" />
              </div>
            </div>

            {/* OTP Verification Modal */}
            {showOTP && (
              <OTPVerification
                type="withdrawal"
                onVerify={handleOTPVerify}
                onCancel={handleOTPCancel}
              />
            )}
  
            {/* Withdrawal Status Alert */}
            {withdrawStatus && (
              <div className={`mb-6 p-4 rounded-lg ${
                withdrawStatus.type === 'processing' ? 'bg-blue-50 text-blue-700' :
                withdrawStatus.type === 'success' ? 'bg-green-50 text-green-700' :
                'bg-red-50 text-red-700'
              }`}>
                <div className="flex items-center">
                  {withdrawStatus.type === 'processing' && (
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  {withdrawStatus.type === 'success' && (
                    <CheckCircleIcon className="h-5 w-5 mr-3" />
                  )}
                  {withdrawStatus.type === 'error' && (
                    <ExclamationCircleIcon className="h-5 w-5 mr-3" />
                  )}
                  <p>{withdrawStatus.message}</p>
                </div>
                
                {withdrawStatus.type === 'success' && (
                  <button 
                    onClick={resetForm}
                    className="mt-3 text-sm font-medium underline"
                  >
                    Make another withdrawal
                  </button>
                )}
              </div>
            )}
  
            {/* Withdrawal Method Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {Object.keys(withdrawMethods).map((method) => (
                <motion.button
                  key={method}
                  onClick={() => setWithdrawMethod(method)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    withdrawMethod === method 
                      ? 'border-primary-500 bg-primary-50' 
                      : 'border-gray-200 hover:border-primary-200'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isProcessing}
                >
                  <h3 className="text-lg font-semibold mb-2">{withdrawMethods[method].title}</h3>
                  <p className="text-sm text-gray-600">{withdrawMethods[method].description}</p>
                </motion.button>
              ))}
            </div>
  
            {/* Withdrawal Form */}
            <div className="bg-gray-50 rounded-lg p-6">
              <form onSubmit={handleWithdrawalSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Withdrawal Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      className="pl-8 pr-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter amount"
                      min="100"
                      max={userData?.balance || 0}
                      disabled={isProcessing}
                    />
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Minimum withdrawal: $100
                  </p>
                </div>
  
                {withdrawMethod === 'crypto' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Cryptocurrency
                    </label>
                    <select 
                      className="w-full p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={isProcessing}
                    >
                      {withdrawMethods.crypto.options.map((option) => (
                        <option key={option.name}>
                          {option.name} (Fee: {option.fee})
                        </option>
                      ))}
                    </select>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Wallet Address
                      </label>
                      <input
                        type="text"
                        value={withdrawAddress}
                        onChange={(e) => setWithdrawAddress(e.target.value)}
                        className="w-full p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter your wallet address"
                        disabled={isProcessing}
                      />
                    </div>
                  </div>
                )}
  
                {withdrawMethod === 'bank' && (
                  <div className="space-y-4">
                    {withdrawMethods.bank.requirements.map((field, idx) => {
                      const key = [
                        'bankName',
                        'accountNumber',
                        'routingNumber',
                        'accountHolder',
                        'bankAddress',
                      ][idx];
                      return (
                        <div key={field}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{field}</label>
                          <input
                            type="text"
                            value={bankDetails[key]}
                            onChange={e => setBankDetails(prev => ({ ...prev, [key]: e.target.value }))}
                            className="w-full p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder={`Enter ${field.toLowerCase()}`}
                            disabled={isProcessing}
                          />
                        </div>
                      );
                    })}
                    <p className="text-sm text-gray-500">Wire Fee: {withdrawMethods.bank.fee}</p>
                  </div>
                )}
  
                {withdrawMethod === 'cashapp' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CashApp $Cashtag</label>
                    <input
                      type="text"
                      value={cashappTag}
                      onChange={e => setCashappTag(e.target.value)}
                      className="w-full p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter your $Cashtag"
                      disabled={isProcessing}
                    />
                    <p className="mt-2 text-sm text-gray-500">Limit: {withdrawMethods.cashapp.limit}<br />Fee: {withdrawMethods.cashapp.fee}</p>
                  </div>
                )}

                {withdrawMethod === 'paypal' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PayPal Email</label>
                    <input
                      type="email"
                      value={withdrawAddress}
                      onChange={e => setWithdrawAddress(e.target.value)}
                      className="w-full p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter your PayPal email"
                      disabled={isProcessing}
                    />
                    <p className="mt-2 text-sm text-gray-500">Limit: {withdrawMethods.paypal.limit}<br />Fee: {withdrawMethods.paypal.fee}</p>
                  </div>
                )}
  
                {/* Email Notification Option */}
                <div className="border-t pt-4">
                  <div className="flex items-center mb-4">
                    <input
                      id="notifyByEmail"
                      type="checkbox"
                      checked={notifyByEmail}
                      onChange={(e) => setNotifyByEmail(e.target.checked)}
                      className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500"
                      disabled={isProcessing}
                    />
                    <label htmlFor="notifyByEmail" className="ml-2 text-sm text-gray-700">
                      Notify me about withdrawal status by email
                    </label>
                  </div>
                  
                  {notifyByEmail && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email for notifications
                      </label>
                      <input
                        type="email"
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                        className="w-full p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter your email address"
                        disabled={isProcessing}
                      />
                    </div>
                  )}
                </div>
  
                <div className="pt-4">
                  <motion.button
                    type="submit"
                    className={`w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center ${
                      isProcessing 
                        ? 'bg-primary-400 cursor-not-allowed' 
                        : 'bg-primary-600 hover:bg-primary-700'
                    } text-white`}
                    whileHover={isProcessing ? {} : { scale: 1.02 }}
                    whileTap={isProcessing ? {} : { scale: 0.98 }}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      'Request Withdrawal'
                    )}
                  </motion.button>
                  <p className="mt-2 text-sm text-gray-500 text-center">
                    Withdrawals are processed within 24 hours
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const DashboardPanel = () => {
    // Calculate days remaining for each active investment
    const activeInvestments = userData?.investments?.filter(inv => inv.status === 'active') || [];
    
    // Get the most recent active investment
    const latestInvestment = activeInvestments.length > 0 
      ? activeInvestments.reduce((latest, current) => {
          const currentDate = new Date(current.startDate);
          const latestDate = new Date(latest.startDate);
          return currentDate > latestDate ? current : latest;
        }, activeInvestments[0])
      : null;

    // Calculate totals from individual investments
    const calculateTotalInvested = () => {
      if (!userData?.investments || userData.investments.length === 0) return 0;
      return userData.investments.reduce((total, inv) => {
        return total + parseFloat(inv.amount || 0);
      }, 0);
    };

    const calculateTotalProfit = () => {
      if (!userData?.investments || userData.investments.length === 0) return 0;
      return userData.investments.reduce((total, inv) => {
        return total + parseFloat(inv.profit || inv.expectedReturn || 0);
      }, 0);
    };

    const calculateTotalBalance = () => {
      if (!userData?.investments || userData.investments.length === 0) return 0;
      return userData.investments.reduce((total, inv) => {
        return total + parseFloat(inv.balance || 0);
      }, 0);
    };

    // Calculate days remaining and progress for the latest investment
    const getDaysRemaining = (investment) => {
      if (!investment) return 0;
      const startDate = new Date(investment.startDate);
      const endDate = new Date(investment.endDate);
      const now = new Date();
      const totalDays = 21;
      const elapsed = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
      return Math.max(0, totalDays - elapsed);
    };

    const getProgress = (investment) => {
      if (!investment) return 0;
      const totalDays = 21;
      const remaining = getDaysRemaining(investment);
      return ((totalDays - remaining) / totalDays) * 100;
    };

    return (
      <div className="space-y-8 pb-8">
        {/* Active Investment Plan - moved to the very top */}
        {latestInvestment ? (
          <button
            className="w-full text-left bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg shadow-lg p-6 focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer hover:brightness-105 hover:shadow-2xl transition-all relative"
            onClick={() => setShowInvestmentsModal(true)}
            title="Click to view all investments"
          >
            {/* Highly visible visual cue for clickability - now at top left */}
            <div className="absolute top-3 left-3 flex items-center space-x-2 bg-white border-2 border-primary-600 px-4 py-2 rounded-full shadow-lg z-20" style={{ minWidth: 'max-content' }}>
              <InformationCircleIcon className="h-6 w-6 text-primary-600" />
              <span className="text-base font-bold text-primary-700">Click to view all investments</span>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="text-white mb-4 md:mb-0">
                <h2 className="text-2xl font-bold mb-2">{latestInvestment.planName}</h2>
                <p className="text-white/80">Investment Amount: ${parseFloat(latestInvestment.amount).toLocaleString()}</p>
                <p className="text-white/80">Expected Return: ${parseFloat(latestInvestment.expectedReturn).toLocaleString()}</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="bg-white/10 rounded-lg p-4 text-center min-w-[120px] mb-3">
                  <span className="text-3xl font-bold text-white">{getDaysRemaining(latestInvestment)}</span>
                  <p className="text-sm text-white/80 mt-1">Days Remaining</p>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2.5">
                  <div 
                    className="bg-green-500 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${getProgress(latestInvestment)}%` }}
                  />
                </div>
                <p className="text-sm text-white/80 mt-2">
                  {Math.round(getProgress(latestInvestment))}% Complete
                </p>
              </div>
            </div>
          </button>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Active Investment Plan</h2>
            <p className="text-gray-600 mb-4">Start your investment journey today!</p>
            <button
              onClick={() => window.open('/investment-plans', '_blank')}
              className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
            >
              View Investment Plans
            </button>
          </div>
        )}

        {/* Investments Modal */}
        <Transition.Root show={showInvestmentsModal} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={setShowInvestmentsModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
              leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-30 transition-opacity" />
            </Transition.Child>
            <div className="fixed inset-0 z-50 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <Dialog.Title as="h3" className="text-lg font-bold leading-6 text-primary-700 mb-4 flex justify-between items-center">
                      All Investments
                      <button onClick={() => setShowInvestmentsModal(false)} className="text-gray-400 hover:text-primary-600 text-2xl font-bold">&times;</button>
                    </Dialog.Title>
                    <div className="divide-y divide-gray-200">
                      {userData?.investments && userData.investments.length > 0 ? (
                        userData.investments.map((inv, idx) => (
                          <div key={inv.id || idx} className="py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div>
                              <div className="font-semibold text-primary-800">{inv.planName}</div>
                              <div className="text-sm text-gray-500">{new Date(inv.startDate).toLocaleDateString()} - {new Date(inv.endDate).toLocaleDateString()}</div>
                              <div className="text-xs text-gray-400">Status: <span className={inv.status === 'active' ? 'text-green-600' : 'text-gray-600'}>{inv.status}</span></div>
                            </div>
                            <div className="flex flex-col md:items-end">
                              <div className="text-sm text-gray-700">Amount: <span className="font-medium">${parseFloat(inv.amount).toLocaleString()}</span></div>
                              <div className="text-sm text-gray-700">Expected Return: <span className="font-medium">${parseFloat(inv.expectedReturn).toLocaleString()}</span></div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-8 text-center text-gray-500">No investments found.</div>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Collapsible Original Stat Cards */}
        <div className="mb-8">
          <button
            className="mb-4 flex items-center space-x-3 text-primary-700 font-semibold focus:outline-none bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 w-full justify-between"
            onClick={() => setShowStatCards((prev) => !prev)}
            aria-expanded={showStatCards}
          >
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                showStatCards ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-600'
              }`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <span className="text-lg">{showStatCards ? 'Hide Summary' : 'Show Summary'}</span>
              <span className="text-sm text-gray-500 font-normal">
                {showStatCards ? 'Click to collapse' : 'Click to expand'}
              </span>
            </div>
            <svg className={`w-5 h-5 transition-transform duration-200 ${showStatCards ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showStatCards && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <motion.div
                  className="bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-lg shadow-lg p-6"
                  whileHover={{ scale: 1.02 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white/90 font-medium">Total Balance</h3>
                    <BanknotesIcon className="h-6 w-6 text-white/80" />
                  </div>
                  <p className="text-3xl font-bold text-white">
                    ${calculateTotalBalance().toLocaleString()}
                  </p>
                  <p className="text-sm text-white/80 mt-2">Current balance</p>
                </motion.div>

                <motion.div
                  className="bg-gradient-to-br from-indigo-400 to-indigo-500 rounded-lg shadow-lg p-6"
                  whileHover={{ scale: 1.02 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white/90 font-medium">Total Invested</h3>
                    <ChartBarIcon className="h-6 w-6 text-white/80" />
                  </div>
                  <p className="text-3xl font-bold text-white">
                    ${calculateTotalInvested().toLocaleString()}
                  </p>
                  <p className="text-sm text-white/80 mt-2">Current investments</p>
                </motion.div>

                <motion.div
                  className="bg-gradient-to-br from-rose-400 to-rose-500 rounded-lg shadow-lg p-6"
                  whileHover={{ scale: 1.02 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white/90 font-medium">Total Profit</h3>
                    <ArrowTrendingUpIcon className="h-6 w-6 text-white/80" />
                  </div>
                  <p className="text-3xl font-bold text-white">
                    ${calculateTotalProfit().toLocaleString()}
                  </p>
                  <p className="text-sm text-white/80 mt-2">Total earnings</p>
                </motion.div>

                <motion.div
                  className="bg-gradient-to-br from-slate-400 to-slate-500 rounded-lg shadow-lg p-6"
                  whileHover={{ scale: 1.02 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-white/90 font-medium">Active Investments</h3>
                    <CircleStackIcon className="h-6 w-6 text-white/80" />
                  </div>
                  <p className="text-3xl font-bold text-white">
                    {userData?.investments?.length || '0'}
                  </p>
                  <p className="text-sm text-white/80 mt-2">Running plans</p>
                </motion.div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Per-Investment Stat Cards - now directly below summary cards */}
        {userData?.investments && userData.investments.length > 0 && (
          <div className="space-y-8 mb-8">
            {userData.investments.map((inv, idx) => (
              <div key={inv.id || idx} className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-primary-800 mb-1">{inv.planName}</h2>
                    <div className="text-sm text-gray-500">{new Date(inv.startDate).toLocaleDateString()} - {new Date(inv.endDate).toLocaleDateString()}</div>
                    <div className="text-xs text-gray-400 mt-1">Status: <span className={inv.status === 'active' ? 'text-green-600' : 'text-gray-600'}>{inv.status}</span></div>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <span className="inline-block bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-xs font-semibold">Investment ID: {inv.id || idx + 1}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <StatCard
                    title="Invested Amount"
                    value={`$${parseFloat(inv.amount).toLocaleString()}`}
                    color="from-blue-500 to-blue-600"
                    subtext="Principal"
                    icon={<ChartBarIcon className="h-6 w-6 text-white/80" />}
                  />
                  <StatCard
                    title="Profit"
                    value={`$${parseFloat(inv.profit || inv.expectedReturn || 0).toLocaleString()}`}
                    color="from-purple-500 to-purple-600"
                    subtext="Total earnings"
                    icon={<ArrowTrendingUpIcon className="h-6 w-6 text-white/80" />}
                  />
                  <StatCard
                    title="Balance"
                    value={`$${parseFloat(inv.balance || 0).toLocaleString()}`}
                    color="from-green-500 to-green-600"
                    subtext="Current balance"
                    icon={<BanknotesIcon className="h-6 w-6 text-white/80" />}
                  />
                  <StatCard
                    title="Status"
                    value={inv.status === 'active' ? 'Active' : 'Completed'}
                    color="from-amber-500 to-amber-600"
                    subtext={inv.status === 'active' ? 'Running plan' : 'Finished'}
                    icon={<CircleStackIcon className="h-6 w-6 text-white/80" />}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* The rest of the dashboard content follows */}
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Recent Activity</h2>
            <button className="text-primary-600 text-sm font-medium hover:text-primary-700">
              View All
            </button>
          </div>
          <div className="space-y-3">
            {userData?.transactions?.slice(0, 5).map((transaction, index) => (
              <motion.div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center space-x-4">
                  <div className={`p-2 rounded-full ${
                    transaction.type === 'deposit' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {transaction.type === 'deposit' ? (
                      <ArrowUpCircleIcon className="h-6 w-6 text-green-600" />
                    ) : (
                      <ArrowDownCircleIcon className="h-6 w-6 text-red-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium">
                      {transaction.type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(transaction.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${
                    transaction.type === 'deposit' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {transaction.type === 'deposit' ? '+' : '-'}${transaction.amount.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">{transaction.status}</p>
                </div>
              </motion.div>
            ))}
            {(!userData?.transactions || userData.transactions.length === 0) && (
              <div className="text-center text-gray-500 py-3">
                No recent activity
              </div>
            )}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 gap-6">
          {/* Portfolio Performance */}
          <div className="bg-white rounded-lg shadow-lg p-4">
            <h2 className="text-lg font-semibold mb-4">Portfolio Performance</h2>
            <PortfolioPerformance 
              portfolioData={[
                { name: 'Total Balance', value: Math.round((calculateTotalBalance() / (calculateTotalInvested() || 1)) * 100) },
                { name: 'Total Invested', value: Math.round((calculateTotalInvested() / (calculateTotalInvested() || 1)) * 100) },
                { name: 'Total Profit', value: Math.round((calculateTotalProfit() / (calculateTotalInvested() || 1)) * 100) },
                { name: 'Active Plans', value: Math.round((userData?.investments?.filter(inv => inv.status === 'active').length || 0) / (userData?.investments?.length || 1) * 100) }
              ]}
              performanceData={userData?.investments?.map((inv, index) => ({
                month: new Date(inv.startDate).toISOString().split('T')[0],
                value: parseFloat(inv.amount || 0),
                profit: parseFloat(inv.profit || inv.expectedReturn || 0)
              })) || []}
              userData={{
                balance: calculateTotalBalance(),
                totalInvested: calculateTotalInvested(),
                totalProfit: calculateTotalProfit(),
                investments: userData?.investments || []
              }}
            />
          </div>

          {/* Trading Overview */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">Market Overview</h2>
                  <p className="text-white/80 mt-1">Real-time cryptocurrency market data</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center px-3 py-1 bg-white/10 rounded-full">
                    <span className="text-sm text-white mr-2">Live Data</span>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                  <div className="text-white text-sm font-semibold">
                    Powered by TradingView
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 p-6">
              {/* Market Summary */}
              <div className="space-y-6">
                {/* Market Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-600">Market Cap</h3>
                      <ArrowTrendingUpIcon className="h-5 w-5 text-green-600" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">$2.89T</p>
                    <p className="text-sm text-green-600 mt-1">+2.4%</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-600">24h Volume</h3>
                      <ChartBarIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">$98.7B</p>
                    <p className="text-sm text-blue-600 mt-1">+5.8%</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-600">BTC Dominance</h3>
                      <CircleStackIcon className="h-5 w-5 text-purple-600" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">48.2%</p>
                    <p className="text-sm text-purple-600 mt-1">-0.3%</p>
                  </div>
                </div>

                {/* Advanced Chart */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">Advanced Trading Chart</h3>
                      <p className="text-sm text-gray-600">Interactive multi-timeframe analysis</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>Professional Tools</span>
                      <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                        <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200">
                    <TradingViewWidget />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <motion.button
            onClick={() => setActiveTab('deposit')}
            className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="bg-green-100 p-3 rounded-full mb-3">
              <ArrowUpCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-sm font-semibold">Deposit</h3>
          </motion.button>

          <motion.button
            onClick={() => setActiveTab('withdraw')}
            className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="bg-red-100 p-3 rounded-full mb-3">
              <ArrowDownCircleIcon className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-sm font-semibold">Withdraw</h3>
          </motion.button>

          <motion.button
            onClick={() => window.open('/investment-plans', '_blank')}
            className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="bg-blue-100 p-3 rounded-full mb-3">
              <ChartBarIcon className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-sm font-semibold">Invest</h3>
          </motion.button>

          <motion.button
            onClick={() => setActiveTab('notifications')}
            className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center relative"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="bg-yellow-100 p-3 rounded-full mb-3">
              <BellIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <h3 className="text-sm font-semibold">Alerts</h3>
            {notifications.filter(n => !n.read).length > 0 && (
              <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                {notifications.filter(n => !n.read).length}
              </span>
            )}
          </motion.button>
        </div>
      </div>
    );
  };

  if (loading || error) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        {loading ? (
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        ) : (
          <div className="text-red-600 text-center p-4">
            <p className="text-xl font-semibold">Error loading dashboard</p>
            <p className="mt-2">{error}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      {/* Background pattern */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          backgroundColor: '#f8fafc',
          backgroundImage: `
            linear-gradient(30deg, #4f46e5 12%, transparent 12.5%, transparent 87%, #4f46e5 87.5%, #4f46e5),
            linear-gradient(150deg, #4f46e5 12%, transparent 12.5%, transparent 87%, #4f46e5 87.5%, #4f46e5),
            linear-gradient(30deg, #4f46e5 12%, transparent 12.5%, transparent 87%, #4f46e5 87.5%, #4f46e5),
            linear-gradient(150deg, #4f46e5 12%, transparent 12.5%, transparent 87%, #4f46e5 87.5%, #4f46e5),
            linear-gradient(60deg, #0ea5e9 25%, transparent 25.5%, transparent 75%, #0ea5e9 75%, #0ea5e9),
            linear-gradient(60deg, #0ea5e9 25%, transparent 25.5%, transparent 75%, #0ea5e9 75%, #0ea5e9)
          `,
          backgroundSize: '80px 140px',
          backgroundPosition: '0 0, 0 0, 40px 70px, 40px 70px, 0 0, 40px 70px',
          opacity: 0.15
        }}
      />

      <div className="min-h-screen">
        <Navigation />
        <Sidebar />
        <div className="lg:ml-64 relative z-10">
          <div className="pt-12 lg:pt-4 px-4 lg:p-8">
            <div className="max-w-7xl mx-auto">
              {activeTab === 'dashboard' && <DashboardPanel />}
              {activeTab === 'deposit' && <DepositPanel />}
              {activeTab === 'withdraw' && <WithdrawPanel />}
              {activeTab === 'notifications' && <NotificationsPanel />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Updated StatCard Component to handle subtext and icon
const StatCard = ({ title, value, color, subtext, icon }) => (
  <div className={`bg-gradient-to-r ${color} rounded-lg shadow-lg p-3 lg:p-6 text-white`}>
    <div className="flex items-center justify-between">
      <h3 className="text-xs lg:text-sm text-white/80">{title}</h3>
      {icon && <div>{icon}</div>}
    </div>
    <p className="text-lg lg:text-2xl font-bold mt-1 lg:mt-2">{value}</p>
    {subtext && (
      <p className="text-xs lg:text-sm mt-1 lg:mt-2 text-white/80">
        {subtext}
      </p>
    )}
  </div>
);

export default UserDashboard; 